import React, { useEffect, useMemo, useState } from 'react';
import {
  PhoneForwarded, Save, Loader2, AlertTriangle, Plus, Trash2, Users, User,
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL || '';

const RING_OPTIONS = [
  { value: 15, label: '15s' },
  { value: 20, label: '20s' },
  { value: 25, label: '25s' },
  { value: 30, label: '30s' },
  { value: 45, label: '45s' },
  { value: 60, label: '60s' },
];

const MAX_SIMUL_NUMBERS = 5;

const looksLikeUSPhone = (raw) => {
  if (!raw) return false;
  const digits = raw.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

const rowId = () => (
  globalThis.crypto?.randomUUID?.() ?? `n-${Math.random().toString(36).slice(2, 10)}`
);

const CallForwardingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState('single');
  const [forwardTo, setForwardTo] = useState('');
  const [numbers, setNumbers] = useState([]); // [{ id, phone, label }]
  const [ringSeconds, setRingSeconds] = useState(25);
  const [businessHoursOnly, setBusinessHoursOnly] = useState(false);
  const [amd, setAmd] = useState(true);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken(false);
        const res = await fetch(`${API}/api/settings/call-forwarding`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await res.json();
        if (cancelled) return;
        if (res.ok && payload?.settings) {
          const s = payload.settings;
          setEnabled(!!s.enabled);
          setMode(s.mode === 'simul' ? 'simul' : 'single');
          setForwardTo(s.forwardTo || '');
          setNumbers(
            (Array.isArray(s.numbers) ? s.numbers : []).map((n) => ({
              id: rowId(),
              phone: n.phone || '',
              label: n.label || '',
            }))
          );
          setRingSeconds(Number(s.ringSeconds) || 25);
          setBusinessHoursOnly(!!s.businessHoursOnly);
          setAmd(s.amd !== false);
          setSavedSnapshot(JSON.stringify(s));
        }
      } catch (err) {
        console.error('[CallForwardingSettings] load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const numbersForApi = useMemo(
    () => numbers
      .map((n) => ({ phone: (n.phone || '').trim(), label: (n.label || '').trim() }))
      .filter((n) => n.phone),
    [numbers]
  );

  const isDirty = useMemo(() => {
    if (!savedSnapshot) return true;
    try {
      const parsed = JSON.parse(savedSnapshot);
      const current = {
        enabled, mode, forwardTo, ringSeconds, businessHoursOnly,
        amd, numbers: numbersForApi,
      };
      const saved = {
        enabled: parsed.enabled,
        mode: parsed.mode === 'simul' ? 'simul' : 'single',
        forwardTo: parsed.forwardTo || '',
        ringSeconds: parsed.ringSeconds,
        businessHoursOnly: !!parsed.businessHoursOnly,
        amd: parsed.amd !== false,
        numbers: (parsed.numbers || []).map((n) => ({ phone: n.phone || '', label: n.label || '' })),
      };
      return JSON.stringify(current) !== JSON.stringify(saved);
    } catch { return true; }
  }, [savedSnapshot, enabled, mode, forwardTo, ringSeconds, businessHoursOnly, amd, numbersForApi]);

  const singleInvalid = enabled && mode === 'single' && !looksLikeUSPhone(forwardTo);
  const simulInvalid = enabled && mode === 'simul' && (
    numbersForApi.length === 0 ||
    numbersForApi.length > MAX_SIMUL_NUMBERS ||
    numbersForApi.some((n) => !looksLikeUSPhone(n.phone))
  );

  const updateNumber = (id, field, value) => {
    setNumbers((prev) => prev.map((n) => (n.id === id ? { ...n, [field]: value } : n)));
  };
  const removeNumber = (id) => {
    setNumbers((prev) => prev.filter((n) => n.id !== id));
  };
  const addNumber = () => {
    if (numbers.length >= MAX_SIMUL_NUMBERS) return;
    setNumbers((prev) => [...prev, { id: rowId(), phone: '', label: '' }]);
  };

  const handleSave = async () => {
    if (saving) return;
    if (singleInvalid) { toast.error('Enter a valid US phone number to forward to.'); return; }
    if (simulInvalid)  { toast.error('Add 1–5 valid US phone numbers for simul-ring.'); return; }

    setSaving(true);
    try {
      const token = await getAuth().currentUser?.getIdToken(false);
      const body = {
        enabled,
        mode,
        ringSeconds,
        businessHoursOnly,
        amd,
      };
      if (mode === 'single') {
        body.forwardTo = forwardTo.trim();
        // INTENTIONALLY don't send `numbers` in single mode — sending `[]`
        // would wipe a previously-configured simul list every time the user
        // saves a single-mode tweak. Same for forwardTo in simul mode.
      } else {
        body.numbers = numbersForApi;
      }

      const res = await fetch(`${API}/api/settings/call-forwarding`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));
      if (res.ok && payload?.success) {
        const s = payload.settings;
        setSavedSnapshot(JSON.stringify(s));
        setForwardTo(s.forwardTo || '');
        setNumbers((s.numbers || []).map((n) => ({
          id: rowId(), phone: n.phone || '', label: n.label || '',
        })));
        toast.success('Call forwarding settings saved');
      } else {
        toast.error(payload?.detail || 'Failed to save');
      }
    } catch (err) {
      console.error('[CallForwardingSettings] save failed:', err);
      toast.error('Network error — please try again');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </section>
    );
  }

  return (
    <section
      className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-5"
      data-testid="call-forwarding-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <PhoneForwarded className="w-5 h-5 text-gray-500 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold text-secondary">Inbound call forwarding</h3>
            <p className="text-xs text-gray-500 mt-0.5 max-w-md">
              Forward inbound calls on your KRO number to one phone, or ring multiple numbers at
              once. If nobody answers, the missed-call text-back still fires automatically.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label htmlFor="call-forwarding-enabled" className="text-sm">Enabled</Label>
          <Switch
            id="call-forwarding-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="call-forwarding-enabled-toggle"
          />
        </div>
      </div>

      {/* Ring mode picker */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="call-forwarding-mode-picker">
        <button
          type="button"
          onClick={() => setMode('single')}
          disabled={!enabled}
          className={`flex items-start gap-2 p-3 rounded-lg border text-left transition ${
            mode === 'single'
              ? 'border-[var(--cb-orange)] bg-[rgba(242,107,29,0.05)]'
              : 'border-gray-200 hover:border-gray-300'
          } ${!enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid="call-forwarding-mode-single"
        >
          <User className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium">Single number</div>
            <div className="text-xs text-gray-500 mt-0.5">Ring one number until answered.</div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setMode('simul')}
          disabled={!enabled}
          className={`flex items-start gap-2 p-3 rounded-lg border text-left transition ${
            mode === 'simul'
              ? 'border-[var(--cb-orange)] bg-[rgba(242,107,29,0.05)]'
              : 'border-gray-200 hover:border-gray-300'
          } ${!enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid="call-forwarding-mode-simul"
        >
          <Users className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium">Multiple numbers (simul-ring)</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Ring up to {MAX_SIMUL_NUMBERS} numbers at once. First to answer wins.
            </div>
          </div>
        </button>
      </div>

      {/* Mode-specific inputs */}
      {mode === 'single' ? (
        <div className="space-y-1.5">
          <Label htmlFor="call-forwarding-number" className="text-xs font-medium">Forward to</Label>
          <Input
            id="call-forwarding-number"
            type="tel"
            inputMode="tel"
            placeholder="+1 555 123 4567"
            value={forwardTo}
            onChange={(e) => setForwardTo(e.target.value)}
            disabled={!enabled}
            className={singleInvalid ? 'border-red-400' : ''}
            data-testid="call-forwarding-number-input"
          />
        </div>
      ) : (
        <div className="space-y-2" data-testid="call-forwarding-numbers-list">
          {numbers.length === 0 && (
            <p className="text-xs text-gray-500">Add at least one number to ring.</p>
          )}
          {numbers.map((n, idx) => {
            const invalid = enabled && n.phone && !looksLikeUSPhone(n.phone);
            return (
              <div
                key={n.id}
                className="grid grid-cols-12 gap-2 items-center"
                data-testid={`call-forwarding-numbers-row-${idx}`}
              >
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="+1 555 123 4567"
                  value={n.phone}
                  onChange={(e) => updateNumber(n.id, 'phone', e.target.value)}
                  disabled={!enabled}
                  className={`col-span-6 ${invalid ? 'border-red-400' : ''}`}
                  data-testid={`call-forwarding-numbers-row-${idx}-phone`}
                />
                <Input
                  type="text"
                  placeholder="Label (e.g. Alex)"
                  value={n.label}
                  maxLength={40}
                  onChange={(e) => updateNumber(n.id, 'label', e.target.value)}
                  disabled={!enabled}
                  className="col-span-5"
                  data-testid={`call-forwarding-numbers-row-${idx}-label`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeNumber(n.id)}
                  disabled={!enabled}
                  className="col-span-1 text-gray-400 hover:text-red-600 px-2"
                  data-testid={`call-forwarding-numbers-row-${idx}-remove`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
          {numbers.length < MAX_SIMUL_NUMBERS && (
            <Button
              variant="outline"
              size="sm"
              onClick={addNumber}
              disabled={!enabled}
              className="w-full"
              data-testid="call-forwarding-numbers-add"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add another number ({numbers.length}/{MAX_SIMUL_NUMBERS})
            </Button>
          )}
        </div>
      )}

      {/* Ring duration */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="space-y-1.5">
          <Label htmlFor="call-forwarding-ring" className="text-xs font-medium">Ring for</Label>
          <Select
            value={String(ringSeconds)}
            onValueChange={(v) => setRingSeconds(Number(v))}
            disabled={!enabled}
          >
            <SelectTrigger id="call-forwarding-ring" data-testid="call-forwarding-ring-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RING_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={String(o.value)} data-testid={`call-forwarding-ring-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Toggles */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm">Only during business hours</Label>
            <p className="text-xs text-gray-500 mt-0.5">
              Outside hours, the call goes straight to missed-call text-back. Uses the same hours as
              your missed-call settings.
            </p>
          </div>
          <Switch
            checked={businessHoursOnly}
            onCheckedChange={setBusinessHoursOnly}
            disabled={!enabled}
            data-testid="call-forwarding-bh-toggle"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm">Skip if voicemail picks up</Label>
            <p className="text-xs text-gray-500 mt-0.5">
              Detect when a forwarded number rolls to voicemail and let the missed-call text-back
              handle it instead.
            </p>
          </div>
          <Switch
            checked={amd}
            onCheckedChange={setAmd}
            disabled={!enabled}
            data-testid="call-forwarding-amd-toggle"
          />
        </div>
      </div>

      {(singleInvalid || simulInvalid) && (
        <div
          className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800"
          data-testid="call-forwarding-invalid-hint"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            {singleInvalid && 'Enter a valid US phone number — e.g. (555) 123-4567 or +15551234567.'}
            {simulInvalid && (
              numbersForApi.length === 0
                ? 'Add at least one valid number to simul-ring.'
                : 'One or more numbers is invalid. Use US E.164 format.'
            )}
          </span>
        </div>
      )}

      {!enabled && (
        <div
          className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800"
          data-testid="call-forwarding-disabled-hint"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Forwarding is off. Inbound calls go straight to missed-call text-back. Toggle on when
            you're ready.
          </span>
        </div>
      )}

      <div className="pt-2">
        <Button
          onClick={handleSave}
          disabled={saving || !isDirty || singleInvalid || simulInvalid}
          size="sm"
          data-testid="call-forwarding-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
    </section>
  );
};

export default CallForwardingSettings;
